import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { MessageSquare, AlertTriangle, Lightbulb, UtensilsCrossed, ConciergeBell, CalendarCheck } from 'lucide-react';

const GuestCommunicationPage = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: MessageSquare,
      title: t('guestCommunication.feature1Title'),
      desc: t('guestCommunication.feature1Desc'),
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      border: 'hover:border-green-500/30',
    },
    {
      icon: AlertTriangle,
      title: t('guestCommunication.feature2Title'),
      desc: t('guestCommunication.feature2Desc'),
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'hover:border-blue-500/30',
    },
    {
      icon: Lightbulb,
      title: t('guestCommunication.feature3Title'),
      desc: t('guestCommunication.feature3Desc'),
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'hover:border-orange-500/30',
    },
    {
      icon: UtensilsCrossed,
      title: t('guestCommunication.feature4Title'),
      desc: t('guestCommunication.feature4Desc'),
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'hover:border-red-500/30',
    },
    {
      icon: ConciergeBell,
      title: t('guestCommunication.feature5Title'),
      desc: t('guestCommunication.feature5Desc'),
      color: 'text-fuchsia-400',
      bg: 'bg-fuchsia-500/10',
      border: 'hover:border-fuchsia-500/30',
    },
    {
      icon: CalendarCheck,
      title: t('guestCommunication.feature6Title'),
      desc: t('guestCommunication.feature6Desc'),
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'hover:border-yellow-500/30',
    },
  ];

  return (
    <div data-testid="guest-communication-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-b from-alertorange/20 to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="w-8 h-8 text-orange-400" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">
                  {t('guestCommunication.title')}
                </span>
              </div>
              <h1 
                data-testid="guest-communication-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
              >
                {t('guestCommunication.subtitle')}
              </h1>
              <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
                {t('guestCommunication.heroDesc')}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="guest-communication-cta"
                  className="btn-demo-cta text-white rounded-lg px-8 py-6"
                >
                  {t('nav.requestDemo')}
                </Button>
              </Link>
            </div>
            <div className="hidden lg:flex justify-start pl-8">>
              <img 
                src="https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/giis9a5g_2E9F4D1F-EF30-47F6-A6B5-BBABED1E8329.png"
                alt="DelFriscos Platform Selection"
                className="w-[420px] h-auto rounded-3xl shadow-2xl shadow-black/50"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={`communication-feature-${idx}`}
                className={`bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 ${feature.border} transition-all duration-300`}
              >
                <div className={`p-3 ${feature.bg} rounded-xl w-fit mb-6`}>
                  <feature.icon className={`w-6 h-6 ${feature.color}`} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuestCommunicationPage;
